import { KebabHorizontalIcon, PencilIcon, TrashIcon, VersionsIcon } from '@primer/octicons-react';
import { GraphQLError } from 'graphql/index';
import { useRouter } from 'next/router';
import React from 'react';
import { graphql, useFragment } from 'react-relay';

import useToast from '../../../hooks/useToast';
import { PromotionActionsMutationButtonStack_promotion$key } from '../../../relay/__generated__/PromotionActionsMutationButtonStack_promotion.graphql';
import { PromotionActionsMutationButtonStack_promotionActivateMutation } from '../../../relay/__generated__/PromotionActionsMutationButtonStack_promotionActivateMutation.graphql';
import { PromotionActionsMutationButtonStack_promotionCopyMutation } from '../../../relay/__generated__/PromotionActionsMutationButtonStack_promotionCopyMutation.graphql';
import { PromotionActionsMutationButtonStack_promotionDeactivateMutation } from '../../../relay/__generated__/PromotionActionsMutationButtonStack_promotionDeactivateMutation.graphql';
import { PromotionActionsMutationButtonStack_promotionDeleteMutation } from '../../../relay/__generated__/PromotionActionsMutationButtonStack_promotionDeleteMutation.graphql';
import { parseGraphQLError } from '../../../utils/error';
import ActionList from '../../core/ActionList';
import ActionMenu from '../../core/ActionMenu';
import DialogButton from '../../core/DialogButton';
import IconButton from '../../core/IconButton';
import MutationConfirmButton from '../../core/MutationConfirmButton';
import Stack from '../../core/Stack';
import PromotionUpdateDialog from '../PromotionUpdateDialog';

const PromotionActionsMutationButtonStack_promotion = graphql`
  fragment PromotionActionsMutationButtonStack_promotion on Promotion {
    id
    actions
    ...PromotionUpdateDialog_promotion
  }
`;

type Props = {
  promotion: PromotionActionsMutationButtonStack_promotion$key;
};

const PromotionActionsMutationButtonStack = ({ promotion: promotionReference }: Props) => {
  const promotion = useFragment(PromotionActionsMutationButtonStack_promotion, promotionReference);
  const router = useRouter();
  const { id, actions } = promotion;

  const { toast } = useToast();

  return (
    <Stack gapX={2}>
      <Stack.Item>
        <MutationConfirmButton<PromotionActionsMutationButtonStack_promotionCopyMutation>
          variant={'outline'}
          mutation={graphql`
            mutation PromotionActionsMutationButtonStack_promotionCopyMutation($input: PromotionCopyInput!) {
              promotionCopy(input: $input) {
                id
              }
            }
          `}
          input={{ promotionId: id }}
          size={'large'}
          config={{
            onCompleted: ({ promotionCopy }) => {
              toast('프로모션 복제가 완료됐어요', 'success');
              router.push(`/promotion/${promotionCopy.id}`);
            },
            onError: () => {
              toast('다시 복제해주세요', 'error');
            },
          }}
          message={'정말 복제할까요?'}
          leadingIcon={VersionsIcon}
        >
          복제하기
        </MutationConfirmButton>
      </Stack.Item>
      {actions.includes('promotion_update') ? (
        <Stack.Item>
          <DialogButton
            size={'large'}
            leadingIcon={PencilIcon}
            renderDialog={({ isOpen, closeDialog }) => (
              <PromotionUpdateDialog
                isOpen={isOpen}
                onDismiss={closeDialog}
                full
                promotion={promotion}
                config={{
                  onCompleted: () => {
                    toast('프로모션 수정이 완료됐어요', 'success');
                    closeDialog();
                  },
                  onError: () => {
                    toast('다시 수정해주세요', 'error');
                  },
                }}
              />
            )}
            variant={'outline'}
          >
            수정하기
          </DialogButton>
        </Stack.Item>
      ) : null}
      {actions.includes('promotion_activate') ? (
        <Stack.Item>
          <MutationConfirmButton<PromotionActionsMutationButtonStack_promotionActivateMutation>
            variant={'primary'}
            mutation={graphql`
              mutation PromotionActionsMutationButtonStack_promotionActivateMutation($input: PromotionActivateInput!) {
                promotionActivate(input: $input) {
                  id
                  ...PromotionStatusLabel_promotion
                  ...PromotionActionsMutationButtonStack_promotion
                }
              }
            `}
            input={{ id }}
            size={'large'}
            message={'프로모션을 활성화할까요?'}
            config={{
              onCompleted: () => {
                toast('프로모션이 활성화됐어요', 'success');
              },
              onError: (error) => {
                toast(parseGraphQLError(error as GraphQLError)?.[0].message || '다시 활성화해주세요', 'error');
              },
            }}
          >
            활성화하기
          </MutationConfirmButton>
        </Stack.Item>
      ) : null}
      {actions.includes('promotion_deactivate') ? (
        <Stack.Item>
          <MutationConfirmButton<PromotionActionsMutationButtonStack_promotionDeactivateMutation>
            variant={'danger'}
            mutation={graphql`
              mutation PromotionActionsMutationButtonStack_promotionDeactivateMutation(
                $input: PromotionDeactivateInput!
              ) {
                promotionDeactivate(input: $input) {
                  id
                  ...PromotionStatusLabel_promotion
                  ...PromotionActionsMutationButtonStack_promotion
                }
              }
            `}
            input={{ id }}
            size={'large'}
            message={'프로모션을 비활성화할까요?'}
            config={{
              onCompleted: () => {
                toast('프로모션이 비활성화됐어요', 'success');
              },
              onError: () => {
                toast('다시 비활성화해주세요', 'error');
              },
            }}
          >
            비활성화하기
          </MutationConfirmButton>
        </Stack.Item>
      ) : null}
      {actions.includes('promotion_delete') ? (
        <Stack.Item>
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} variant={'plain'} aria-label="Open column options" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.MutationItem<PromotionActionsMutationButtonStack_promotionDeleteMutation>
                  mutation={graphql`
                    mutation PromotionActionsMutationButtonStack_promotionDeleteMutation(
                      $input: PromotionDeleteInput!
                    ) {
                      promotionDelete(input: $input) {
                        id @deleteRecord
                      }
                    }
                  `}
                  input={{ id }}
                  variant={'danger'}
                  config={{
                    onCompleted: () => {
                      router.replace('/promotion').then(() => toast('프로모션 삭제 완료!', 'success'));
                    },
                    onError: () => {
                      toast('다시 삭제해주세요', 'error');
                    },
                  }}
                  message={'정말 삭제할까요?'}
                >
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  삭제하기
                </ActionList.MutationItem>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </Stack.Item>
      ) : null}
    </Stack>
  );
};

export default PromotionActionsMutationButtonStack;
